import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Leaf, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Community", to: "/community" },
  { label: "Journal", to: "/about" },
  { label: "About", to: "/about" },
];

export default function Navbar({ overlay = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={[
        "fixed left-0 right-0 top-0 z-50 transition-all duration-300",
        solid
          ? "border-b border-[#5E594E]/70 bg-[#3D3A33]/95 backdrop-blur-md"
          : "bg-transparent",
      ].join(" ")}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 sm:gap-3"
          data-testid="navbar-logo"
        >
          <img
            src="/logo-transparent.png"
            alt="Coastal Roots Botanica logo"
            className="h-7 w-7 shrink-0 sm:h-8 sm:w-8"
            width={32}
            height={32}
          />
          <span
            className={[
              "hidden font-serif text-xl tracking-wide sm:inline sm:text-2xl",
              solid ? "text-[#F5EFE2]" : "text-[#F5EFE2] drop-shadow-md",
            ].join(" ")}
          >
            Coastal Roots Botanica
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to + item.label}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                [
                  "text-sm tracking-wide transition-colors",
                  solid ? "text-[#F5EFE2]/85" : "text-[#F5EFE2]/95 drop-shadow",
                  isActive
                    ? "text-[#C9A961]"
                    : "hover:text-[#C9A961]",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link to="/community">
            <Button
              data-testid="nav-join-cta"
              size="sm"
              className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            >
              Join $50/mo
            </Button>
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-2 text-[#F5EFE2] md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="border-t border-[#5E594E]/70 bg-[#3D3A33] md:hidden"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                className="text-base text-[#F5EFE2]/90 hover:text-[#C9A961]"
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/community">
              <Button className="w-full bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]">
                Join $50/mo
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
